import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Dialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddClienteMutation } from "@/features/auxiliar/clientes/api/add-cliente";
import { AddSimpleClienteMutation } from "@/features/auxiliar/clientes/api/add-simple-cliente";
import { DeleteClienteMutation } from "@/features/auxiliar/clientes/api/delete-cliente";
import { EditClienteMutation } from "@/features/auxiliar/clientes/api/edit-cliente";
import { getAllClientes, useGetAllClientesQuery } from "@/features/auxiliar/clientes/api/get-all-clientes";
import { GetClienteQuery } from "@/features/auxiliar/clientes/api/get-cliente";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { GetCuentaCorrienteQuery } from "@/features/auxiliar/cuenta-corrientes/api/get-cuenta-corriente";
import ViewCuentaCorriente from "@/features/auxiliar/cuenta-corrientes/components/view-cuenta-corriente";
import { SchemaFormHook } from "@/components/form";
import AddClienteForm from "@/features/auxiliar/clientes/components/add-cliente-form";
import EditClienteForm from "@/features/auxiliar/clientes/components/edit-cliente-form";
import SimpleClienteForm, { simpleClienteFormSchema } from "@/features/auxiliar/clientes/components/simple-cliente-form";
import { addClienteFormSchema } from "@/features/auxiliar/clientes/components/add-cliente-form.schema";
import { useOperationalMode } from "@/features/configuration/hooks/use-operational-mode";
import { OperationalMode } from "@/shared/types/operational-mode";

export default function ClientesPage() {
    const openModal = useOpenModal();
    const hasWritePermiso = useHasPermisos(Permiso.WriteClientes);
    const hasReadCuentaCorrientePermiso = useHasPermisos(Permiso.ReadCuentaCorrientes);
    const { mode } = useOperationalMode();

    // Determinar si es modo simplificado (modo 1 = Gestion)
    const isSimpleMode = mode === OperationalMode.Gestion;
    // Si es modo 2 o 3, usar el modo completo
    const isFullMode = mode === OperationalMode.GestionConFacturacion || mode === OperationalMode.Facturacion;

    const filterData = useFilters({
        filters: [
            { type: "string", label: "RUC", name: "ruc" },
            { type: "string", label: "Razón Social", name: "razonSocial" },
            { type: "string", label: "Nombre Fantasía", name: "nombreFantasia" },
            { type: "string", label: "Ciudad", name: "ciudad" },
            { type: "string", label: "Email", name: "email" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllClientesQuery(filterData.filterData);

    const columnData = useColumns({
        columns: [
            { key: "ruc", header: "RUC", render: row => formatString(row.ruc) },
            { key: "dv", header: "DV", render: row => formatString(row.dv) },
            { key: "numDocumento", header: "Documento", render: row => formatString(row.numDocumento) },
            { key: "razonSocial", header: "Razón Social", render: row => formatString(row.nombre) },
            { key: "nombreFantasia", header: "Nombre Fantasía", render: row => formatString(row.nombreFantasia), show: false },
            { key: "tipoContribuyenteTexto", header: "Tipo", render: row => formatString(row.tipoContribuyenteTexto) },
            { key: "ciudad", header: "Ciudad", render: row => formatString(row.ciudad?.nombre) },
            { key: "telefono", header: "Teléfono", render: row => formatString(row.telefono) },
            { key: "email", header: "Email", render: row => formatString(row.email) },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ],
        key: "clientes"
    });

    return <ListPage>
        <ListPage.Header>
            <ListPage.Header.Actions title="Clientes">
                <ListPage.Header.Actions.FilterButton />
                <ListPage.Header.Actions.ColumnButton columnData={columnData} />
                {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
                <ListPage.Header.Actions.Divider />
                <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
                    <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
                    <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
                    <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
                </ListPage.Header.Actions.ExportButton>
            </ListPage.Header.Actions>
            <ListPage.Header.Filters filterData={filterData} />
        </ListPage.Header>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    hasWritePermiso && {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            {hasReadCuentaCorrientePermiso && <DataTable.EntryOption.Button Icon={AccountBalanceIcon} title="Cuenta Corriente" onClick={() => openModal("viewCuentaCorriente", row.personaId)} />}
                            <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
                            <DataTable.EntryOption.DeleteButton disabled={row.protegido} onClick={() => openModal("delete", row)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        {/* Dialog para modo simplificado (Gestión solamente - modo 1) */}
        {isSimpleMode && (
            <Drawer code="add">
                <ModalHeader>Agregar Cliente</ModalHeader>
                <AddSimpleClienteMutation>
                    {mutation => <SchemaFormHook
                        schema={simpleClienteFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("El cliente se ha agregado correctamente.");
                        }}
                        closeModalOnSubmit
                        submitOnce
                    >
                        {form => <SimpleClienteForm form={form} margin={3} />}
                    </SchemaFormHook>}
                </AddSimpleClienteMutation>
            </Drawer>
        )}

        {/* Dialog para modo completo (Gestión + Facturación o Solo Facturación - modo 2 o 3) */}
        {isFullMode && (
            <Dialog code="add" size="md">
                <ModalHeader>Agregar Cliente</ModalHeader>
                <AddClienteMutation>
                    {mutation => <SchemaFormHook
                        schema={addClienteFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("El cliente se ha agregado correctamente.");
                        }}
                        closeModalOnSubmit
                        submitOnce
                    >
                        {form => <AddClienteForm
                            form={form}
                            margin={3}
                        />}
                    </SchemaFormHook>}
                </AddClienteMutation>
            </Dialog>
        )}

        {isSimpleMode && (
            <Drawer code="edit">
                <ModalHeader>Editar Cliente</ModalHeader>
                <ModalValueExtractor>
                    {clienteId => <GetClienteQuery id={clienteId}>
                        {query => <DataFetcher flex={1} query={query}>
                            {cliente => <EditClienteMutation id={cliente.id}>
                                {mutation => <SchemaFormHook
                                    schema={simpleClienteFormSchema}
                                    onSubmit={async ({ value }) => {
                                        await mutation.mutateAsync({
                                            numeroDocumento: value.numeroDocumento,
                                            razonSocial: value.razonSocial,
                                            direccion: value.direccion,
                                            email: value.email,
                                            telefono: value.telefono,
                                        });
                                        notify("El cliente se ha editado correctamente.");
                                    }}
                                    defaultValues={{
                                        numeroDocumento: cliente.numDocumento,
                                        razonSocial: cliente.nombre,
                                        email: cliente.email || "",
                                        telefono: cliente.telefono || "",
                                        direccion: cliente.direccion || "",
                                    }}
                                    closeModalOnSubmit
                                    submitOnce
                                >
                                    {form => <SimpleClienteForm
                                        form={form}
                                        margin={3}
                                        isEditMode={true}
                                        currentNumeroDocumento={cliente.numDocumento}
                                    />}
                                </SchemaFormHook>}
                            </EditClienteMutation>}
                        </DataFetcher>}
                    </GetClienteQuery>}
                </ModalValueExtractor>
            </Drawer>
        )}

        {isFullMode && (
            <Dialog code="edit" size="md">
                <ModalHeader>Editar Cliente</ModalHeader>
                <ModalValueExtractor>
                    {clienteId => <GetClienteQuery id={clienteId}>
                        {query => <DataFetcher flex={1} query={query}>
                            {cliente => <EditClienteMutation id={cliente.id} isFe={true}>
                                {mutation => <SchemaFormHook
                                    schema={addClienteFormSchema}
                                    onSubmit={async ({ value }) => {
                                        await mutation.mutateAsync(value);
                                        notify("El cliente se ha editado correctamente.");
                                    }}
                                    defaultValues={{
                                        ...cliente,
                                        id: cliente.id,
                                        razonSocial: cliente.nombre,
                                        numeroDocumento: cliente.numDocumento,
                                        ciudadId: cliente.ciudad?.id ?? "",
                                        ciudadSeleccionada: cliente.ciudad?.nombre,
                                        departamentoId: cliente.departamento?.id ?? "",
                                        departamentoSeleccionado: cliente.departamento?.nombre,
                                        paisId: cliente.pais && cliente.pais.id,
                                        codigoPais: cliente.codigoPais,
                                        email: cliente.email || "",
                                    }}
                                    closeModalOnSubmit
                                    submitOnce
                                >
                                    {form => <EditClienteForm form={form} padding={3} />}
                                </SchemaFormHook>}
                            </EditClienteMutation>}
                        </DataFetcher>}
                    </GetClienteQuery>}
                </ModalValueExtractor>
            </Dialog>
        )}

        <ConfirmDialog code="delete">
            <ModalValueExtractor>
                {cliente => <DeleteClienteMutation id={cliente.id}>
                    {mutation => <ConfirmDialog.Content
                        title={`Borrar Cliente "${cliente.nombre}"`}
                        description={`¿Estás seguro que quieres borrar el cliente "${cliente.nombre}"?`}
                        onConfirm={async () => {
                            await mutation.mutateAsync({});
                            notify(`El cliente "${cliente.nombre}" se ha borrado correctamente.`, { variant: "success" });
                        }}
                    />}
                </DeleteClienteMutation>}
            </ModalValueExtractor>
        </ConfirmDialog>

        <Dialog code="viewCuentaCorriente" size="lg" fullHeight>
            <ModalValueExtractor>
                {personaId => <GetCuentaCorrienteQuery personaId={personaId}>
                    {query => <DataFetcher query={query}>
                        {cuenta => <>
                            <ModalHeader>Cuenta Corriente - {cuenta.personaNombre}</ModalHeader>
                            <ViewCuentaCorriente cuenta={cuenta} flex={1} padding={3} />
                        </>}
                    </DataFetcher>}
                </GetCuentaCorrienteQuery>}
            </ModalValueExtractor>
        </Dialog>

        <PrintExportDialog
            code="print"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllClientes}
            defaultFileTitle="Clientes"
        />

        <CsvExportDialog
            code="csv"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllClientes}
            defaultFileTitle="Clientes"
        />

        <XlsxExportDialog
            code="xlsx"
            columns={columnData.columns}
            filterData={filterData}
            fetchFn={getAllClientes}
            defaultFileTitle="Clientes"
        />
    </ListPage>
}